import { WORLD_SIZE } from "./WORLD_SIZE";

// Плотность пути игрока в сетке 256×256 с гауссовым ядром; яркость — по логарифму
// от максимума за весь матч, поэтому «до момента» не пересвечивается.
const CELLS = 256;
const KERNEL_RADIUS = 4;
const KERNEL_SIGMA = 1.9;
const KERNEL: readonly (readonly [number, number, number])[] = Array.from(
  { length: (2 * KERNEL_RADIUS + 1) ** 2 },
  (_, index) => {
    const dx = (index % (2 * KERNEL_RADIUS + 1)) - KERNEL_RADIUS;
    const dy = Math.floor(index / (2 * KERNEL_RADIUS + 1)) - KERNEL_RADIUS;
    return [dx, dy, Math.exp(-(dx * dx + dy * dy) / (2 * KERNEL_SIGMA * KERNEL_SIGMA))] as const;
  },
);

export class HeatGrid {
  static readonly size = CELLS;

  private readonly grid = new Float32Array(CELLS * CELLS);
  private readonly logMax: number;
  // Сколько первых точек пути сейчас в сетке.
  private count: number;

  constructor(private readonly points: readonly (readonly [number, number] | null)[]) {
    points.forEach((point) => {
      this.add(point, 1);
    });
    this.count = points.length;
    this.logMax = Math.log1p(this.grid.reduce((max, value) => Math.max(max, value), 0));
  }

  // Оставить в сетке первые `count` точек: добавляет или убирает только разницу.
  setCount(count: number): void {
    const target = Math.max(0, Math.min(this.points.length, count));
    while (this.count < target) {
      this.add(this.points[this.count] ?? null, 1);
      this.count += 1;
    }
    while (this.count > target) {
      this.count -= 1;
      this.add(this.points[this.count] ?? null, -1);
    }
  }

  // Раскраска в `image` (CELLS×CELLS): от тёмно-оранжевого к светло-жёлтому, слабое — прозрачно.
  paint(image: Pick<ImageData, "data">): void {
    const out = image.data;
    for (let i = 0; i < this.grid.length; i += 1) {
      const value = this.grid[i] ?? 0;
      const t = value > 0.05 && this.logMax > 0 ? Math.log1p(value) / this.logMax : 0;
      const k = i * 4;
      if (t < 0.22) {
        out[k + 3] = 0;
        continue;
      }
      out[k] = 255;
      out[k + 1] = 95 + 150 * t;
      out[k + 2] = t > 0.8 ? 40 + 600 * (t - 0.8) : 30;
      out[k + 3] = 55 + (165 * (t - 0.22)) / 0.78;
    }
  }

  private add(point: readonly [number, number] | null, sign: number): void {
    if (point === null) {
      return;
    }
    const cx = Math.round((point[0] / WORLD_SIZE) * CELLS);
    const cy = Math.round((point[1] / WORLD_SIZE) * CELLS);
    for (const [dx, dy, weight] of KERNEL) {
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && y >= 0 && x < CELLS && y < CELLS) {
        this.grid[y * CELLS + x] = (this.grid[y * CELLS + x] ?? 0) + sign * weight;
      }
    }
  }
}
